import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import Swal from "sweetalert2"

export default class extends Controller {
  static targets = ["login", "splash", "loginFooter", "form", "password", "error", "submit", "offline"]
  static values = {
    authenticated: Boolean, remembered: Boolean,
    entryUrl: String, sessionUrl: String, rememberUrl: String, statusUrl: String, workerUrl: String
  }

  connect() {
    this.standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register(this.workerUrlValue, { scope: "/admin/", updateViaCache: "none" }).catch(() => {})
    }
    if (!this.standalone) return

    this.active = true
    if (this.hasLoginTarget) this.loginTarget.hidden = false
    if (this.hasSplashTarget) this.splashTarget.hidden = true
    if (this.hasLoginFooterTarget) this.loginFooterTarget.hidden = false
    this.onVisible = () => { if (!document.hidden) this.checkSession() }
    this.onPageShow = (event) => { if (event.persisted) this.checkSession() }
    this.onOnline = () => { this.updateOffline(); this.checkSession() }
    this.onOffline = () => this.updateOffline()
    this.onLink = (event) => this.keepAdminLinkInApp(event)
    this.onTurboRequest = (event) => {
      const url = event.detail.url
      if (url.origin === window.location.origin && (url.pathname === "/admin" || url.pathname.startsWith("/admin/"))) {
        event.detail.fetchOptions.headers["X-Admin-PWA"] = "standalone"
      }
    }
    document.addEventListener("visibilitychange", this.onVisible)
    window.addEventListener("pageshow", this.onPageShow)
    window.addEventListener("online", this.onOnline)
    window.addEventListener("offline", this.onOffline)
    document.addEventListener("click", this.onLink, true)
    document.addEventListener("turbo:before-fetch-request", this.onTurboRequest)
    this.updateOffline()
    if (this.authenticatedValue && !this.rememberedValue) this.rememberDevice()
  }

  disconnect() {
    this.active = false
    document.removeEventListener("visibilitychange", this.onVisible)
    window.removeEventListener("pageshow", this.onPageShow)
    window.removeEventListener("online", this.onOnline)
    window.removeEventListener("offline", this.onOffline)
    document.removeEventListener("click", this.onLink, true)
    document.removeEventListener("turbo:before-fetch-request", this.onTurboRequest)
  }

  async signIn(event) {
    event.preventDefault()
    if (!this.standalone || this.signingIn || !this.formTarget.reportValidity()) return
    this.signingIn = true
    this.errorTarget.hidden = true
    this.submitTarget.disabled = true
    try {
      const result = await this.request(this.sessionUrlValue, { method: "POST", body: new FormData(this.formTarget) })
      Turbo.cache.clear()
      window.location.replace(result.redirect_to)
    } catch (error) {
      this.errorTarget.textContent = error.message
      this.errorTarget.hidden = false
    } finally {
      this.signingIn = false
      this.submitTarget.disabled = false
    }
  }

  togglePassword(event) {
    const show = this.passwordTarget.type === "password"
    this.passwordTarget.type = show ? "text" : "password"
    event.currentTarget.textContent = show ? "Hide" : "Show"
    event.currentTarget.setAttribute("aria-label", show ? "Hide password" : "Show password")
    event.currentTarget.setAttribute("aria-pressed", String(show))
  }

  async rememberDevice() {
    if (this.remembering || !navigator.onLine) return
    this.remembering = true
    try {
      await this.request(this.rememberUrlValue, { method: "POST" })
      this.rememberedValue = true
    } catch (error) {
      if (!this.active) return
      if (error.status === 401) return this.returnToSignIn()
      Swal.fire({ icon: "warning", title: "Signed in for now", text: "We couldn’t keep this device signed in. Please reopen the app with an internet connection to try again." })
    } finally {
      this.remembering = false
    }
  }

  // Foreground/connection checks only. No timer, background polling or replay of
  // business actions (particularly payouts and campaign sends).
  async checkSession() {
    if (!this.authenticatedValue || this.checking || !navigator.onLine) return
    this.checking = true
    try {
      const result = await this.request(this.statusUrlValue)
      if (!this.active) return
      if (!result.authenticated) return this.returnToSignIn()
      if (!result.remembered) await this.rememberDevice()
    } catch (error) {
      if (this.active && error.status === 401) this.returnToSignIn()
    } finally {
      this.checking = false
    }
  }

  async signOut(event) {
    if (!this.standalone) return // Normal browser logout retains its existing route.
    event.preventDefault()
    if (this.signingOut) return
    this.signingOut = true
    try {
      await this.request(this.sessionUrlValue, { method: "DELETE" })
      this.returnToSignIn()
    } catch (error) {
      Swal.fire({ icon: "error", title: "Not signed out yet", text: error.message })
    } finally {
      this.signingOut = false
    }
  }

  returnToSignIn() {
    Turbo.cache.clear()
    window.location.replace(this.entryUrlValue)
  }

  updateOffline() {
    if (this.hasOfflineTarget) this.offlineTarget.hidden = navigator.onLine
  }

  keepAdminLinkInApp(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const link = event.target.closest("a[target='_blank']")
    if (!link || link.hasAttribute("download")) return
    const url = new URL(link.href, window.location.href)
    if (url.origin === window.location.origin && (url.pathname === "/admin" || url.pathname.startsWith("/admin/"))) {
      link.removeAttribute("target")
    }
  }

  async request(url, options = {}) {
    let response
    try {
      response = await fetch(url, {
        credentials: "same-origin", cache: "no-store", ...options,
        headers: { Accept: "application/json", "X-Admin-PWA": "standalone", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" }
      })
    } catch (_error) {
      throw new Error("Please check your internet connection and try again.")
    }
    const result = await response.json().catch(() => ({}))
    if (!response.ok || response.redirected) {
      const error = new Error(result.error || "We couldn’t complete that request. Please reopen the app and try again.")
      error.status = response.status
      throw error
    }
    if (result.csrf_token) {
      const meta = document.querySelector("meta[name='csrf-token']")
      if (meta) meta.content = result.csrf_token
      document.querySelectorAll("input[name='authenticity_token']").forEach((input) => { input.value = result.csrf_token })
    }
    return result
  }
}
